import { format } from 'date-fns';
import {
    Calendar as PrimeCalendar,
    type CalendarProps as PrimeCalendarProps,
} from 'primereact/calendar';
import { FormEvent } from 'primereact/ts-helpers';
import { forwardRef, ReactNode, Ref } from 'react';
import { Controller, useFormContext } from 'react-hook-form';
import {
    buildRhfRules,
    type FieldValidate,
    mergeRefs,
    warnAboutRegisterSpread,
} from '../../_utils/rhf-ref';
import { Icon } from '../Icon/Icon';
import {
    InputWrapper,
    InputWrapperSharedProps,
} from '../InputWrapper/InputWrapper';

export type CalendarChangeEvent = FormEvent<Date>;

type CalendarProps = PrimeCalendarProps &
    InputWrapperSharedProps & {
        formatStr?: string;
        /**
         * Custom react-hook-form validation function
         */
        validate?: FieldValidate;
    };

type CalendarIconProps = Pick<
    PrimeCalendarProps,
    'showIcon' | 'icon' | 'iconPos' | 'panelClassName'
>;

function getDefaultIconProps(props: CalendarProps): CalendarIconProps {
    const applyDefaultSetting = props.showIcon === undefined;
    if (!applyDefaultSetting) {
        return {};
    }

    return {
        showIcon: true,
        icon: <Icon name="calendar_today" />,
        iconPos: 'right',
        panelClassName: 'icon-right',
    };
}

function CalendarComponent(
    props: CalendarProps,
    ref: Ref<PrimeCalendar>,
): ReactNode {
    const { panelClassName, ...iconProps } = getDefaultIconProps(props);
    const {
        name,
        value,
        horizontal,
        labelClassName,
        onBlur,
        placeholder: placeholderProps,
        clearButtonClassName: clearButtonClassNameProps,
        todayButtonClassName: todayButtonClassNameProps,
        formatStr,
        onHide: onHideProp,
        validate,
        'data-testid': dataTestId,
        ...restProps
    } = props;

    const testId = dataTestId || name;

    const placeholder = placeholderProps || 'Select date';
    const clearButtonClassName = clearButtonClassNameProps || 'p-button-text';
    const todayButtonClassName = todayButtonClassNameProps || 'p-button-text';

    const defaultProps: PrimeCalendarProps = {
        placeholder,
        clearButtonClassName,
        todayButtonClassName,
        showButtonBar: props.showButtonBar ?? true,
    };

    const formContext = useFormContext();
    const isControlled = typeof value !== 'undefined';
    const isRHFControlled = !isControlled && Boolean(formContext && name);
    warnAboutRegisterSpread({
        componentName: 'Calendar',
        isRHFControlled,
        onChange: restProps.onChange,
        ref,
    });
    if (!isRHFControlled) {
        const {
            wrapperClassName: _,
            labelClassName: ___,
            labelTooltip: ____,
            errorClassName: _____,
        } = props;

        return (
            <InputWrapper {...props}>
                <PrimeCalendar
                    ref={ref}
                    name={name}
                    pt={{ input: { 'data-testid': testId } }}
                    invalid={props.invalid}
                    required={props.required}
                    panelClassName={panelClassName}
                    dateFormat="dd/mm/yy"
                    hourFormat="24"
                    appendTo="self"
                    value={value ? new Date(value) : undefined}
                    placeholder={placeholder}
                    {...restProps}
                    {...iconProps}
                    {...defaultProps}
                    onHide={onHideProp}
                    onBlur={onBlur}
                />
            </InputWrapper>
        );
    }

    function valueToDate(value: string | Date | undefined): Date | undefined {
        if (!value) {
            return;
        }

        const convertedDate = new Date(value);
        if (isNaN(convertedDate.getTime())) {
            return;
        }

        return convertedDate;
    }

    return (
        <Controller
            name={name!}
            control={formContext!.control}
            rules={buildRhfRules({ required: props.required, validate })}
            render={({ field, fieldState }) => (
                <InputWrapper
                    field={field}
                    fieldState={fieldState}
                    horizontal={horizontal}
                    labelClassName={labelClassName}
                    {...props}
                >
                    <PrimeCalendar
                        pt={{ input: { 'data-testid': testId } }}
                        panelClassName={panelClassName}
                        dateFormat="dd/mm/yy"
                        hourFormat="24"
                        appendTo="self"
                        placeholder={placeholder}
                        {...field}
                        {...restProps}
                        {...iconProps}
                        {...defaultProps}
                        invalid={fieldState.invalid}
                        ref={mergeRefs(ref, field.ref)}
                        value={valueToDate(field?.value || props?.value)}
                        onChange={(e) => {
                            if (formatStr) {
                                const formatted = e.value
                                    ? format(e.value, formatStr)
                                    : undefined;
                                field.onChange(formatted);
                                return;
                            }

                            field.onChange(e);
                        }}
                        onHide={() => {
                            field.onBlur();
                            onHideProp?.();
                        }}
                        onBlur={(event) => {
                            field.onBlur();
                            onBlur?.(event);
                        }}
                    />
                </InputWrapper>
            )}
        />
    );
}

export const Calendar = forwardRef(CalendarComponent);
